import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 20px;
`;

const RegisterContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 30px 20px;
    margin: 20px;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 30px;
  color: #333;
  font-size: 32px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 15px;
  border: 2px solid #e1e5e9;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  color: white;
  padding: 15px 30px;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 107, 53, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LinkText = styled.p`
  text-align: center;
  margin-top: 20px;
  color: #666;
  
  a {
    color: #ff6b35;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 20px;
  border-left: 4px solid #c33;
`;

const SuccessMessage = styled.div`
  background: #efe;
  color: #3c3;
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 20px;
  border-left: 4px solid #3c3;
`;

const ReferralInfo = styled.div`
  background: #f0f8ff;
  border: 2px solid #4a90e2;
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
  text-align: center;
`;

const ReferralTitle = styled.div`
  font-weight: 600;
  color: #4a90e2;
  margin-bottom: 8px;
  font-size: 16px;
`;

const ReferralName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #333;
`;

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [loadingReferrer, setLoadingReferrer] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  // Загрузка информации о пригласившем при загрузке страницы
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      loadReferrerInfo(refCode);
    }
  }, [searchParams]);

  // Функция для загрузки информации о пригласившем
  const loadReferrerInfo = async (code) => {
    if (!code) return;
    
    setLoadingReferrer(true);
    try {
      const response = await fetch(`/api/referrals/referrer/${code}`);
      if (response.ok) {
        const data = await response.json();
        setReferrerInfo(data.referrer);
      } else {
        console.log('Реферальный код не найден или недействителен');
      }
    } catch (error) {
      console.error('Error loading referrer info:', error);
    } finally {
      setLoadingReferrer(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (formData.password.length < 6) {
      setError('Пароль должен быть не менее 6 символов');
      return;
    }

    setLoading(true);
    try {
      // Добавляем реферальный код к данным формы
      const registrationData = {
        ...formData,
        referralCode: referralCode || null
      };
      
      const result = await register(registrationData);
      
      if (result.success) {
        if (result.requiresVerification) {
          // Редирект на страницу подтверждения email
          const params = new URLSearchParams();
          if (result.userId) params.append('userId', result.userId);
          if (result.email) params.append('email', result.email);
          // Передаем код из ответа, если он есть (режим разработки)
          if (result.verificationCode) {
            params.append('code', result.verificationCode);
          }
          navigate(`/verify-email?${params.toString()}`);
        } else {
          setSuccess('Регистрация успешна! Перенаправляем на главную страницу...');
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
      } else {
        setError(result.message || 'Произошла ошибка при регистрации');
      }
    } catch (error) {
      setError(error.message || 'Произошла ошибка при регистрации');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <RegisterContainer>
        <Title>Регистрация</Title>
        
        {referrerInfo && (
          <ReferralInfo>
            <ReferralTitle>Вас пригласил:</ReferralTitle>
            <ReferralName>{referrerInfo.fullName}</ReferralName>
          </ReferralInfo>
        )}
        
        {loadingReferrer && (
          <div style={{textAlign: 'center', padding: '10px', color: '#666'}}>
            Проверяем приглашение...
          </div>
        )}
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="firstName">Имя</Label>
            <Input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Введите ваше имя"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="lastName">Фамилия</Label>
            <Input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Введите вашу фамилию"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Введите email"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="phone">Телефон</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+7 (999) 123-45-67"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Пароль</Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Введите пароль"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirmPassword">Подтверждение пароля</Label>
            <Input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Подтвердите пароль"
              required
            />
          </InputGroup>

          <Button type="submit" disabled={loading}>
            {loading ? 'Регистрация...' : 'Зарегистрироваться'}
          </Button>
        </Form>

        <LinkText>
          Уже есть аккаунт? <Link to="/login">Войти</Link>
        </LinkText>
      </RegisterContainer>
    </PageContainer>
  );
};

export default RegisterPage; 